import { useCallback, useEffect, useRef, useState, RefObject } from 'react'
import { PaperSize, Orientation, PrintScale, MarginSize } from './paperConfig'
import { calculatePageBreaks, clearPrintBreaks, insertPrintBreaks, PageBreak } from './pageBreakUtils'

interface UsePageBreaksOptions {
  contentRef: RefObject<HTMLDivElement | null>
  enabled: boolean
  paperSize: PaperSize
  orientation: Orientation
  scale: PrintScale
  margin: MarginSize
  forcedBreakSectionIds?: Set<number>
}

interface UsePageBreaksResult {
  breaks: PageBreak[]
  ready: boolean
}

const EMPTY: PageBreak[] = []
const SETTLE_DELAY = 500

const usePageBreaks = ({
  contentRef,
  enabled,
  paperSize,
  orientation,
  scale,
  margin,
  forcedBreakSectionIds
}: UsePageBreaksOptions): UsePageBreaksResult => {
  const [data, setData] = useState<PageBreak[]>(EMPTY)
  const [ready, setReady] = useState(false)
  const prevForcedRef = useRef(forcedBreakSectionIds)

  const calculate = useCallback(() => {
    const contentEl = contentRef.current
    if (!contentEl) return

    clearPrintBreaks()
    const result = calculatePageBreaks({
      contentEl,
      paperSize,
      orientation,
      scale,
      margin,
      forcedBreakSectionIds: forcedBreakSectionIds ?? new Set<number>()
    })
    insertPrintBreaks(result)
    setData(result)
    setReady(true)
  }, [contentRef, paperSize, orientation, scale, margin, forcedBreakSectionIds])

  useEffect(() => {
    if (!enabled || !contentRef.current) {
      clearPrintBreaks()
      setData(EMPTY)
      setReady(false)
      return undefined
    }

    const onlyForcedChanged = prevForcedRef.current !== forcedBreakSectionIds
    prevForcedRef.current = forcedBreakSectionIds

    if (onlyForcedChanged) {
      calculate()
      return undefined
    }

    setReady(false)
    const timer = setTimeout(() => {
      requestAnimationFrame(calculate)
    }, SETTLE_DELAY)
    return () => clearTimeout(timer)
  }, [enabled, paperSize, orientation, scale, margin, forcedBreakSectionIds, contentRef, calculate])

  return { breaks: data, ready }
}

export default usePageBreaks
